import React, { useEffect, useRef } from 'react';
import { usePerformanceManager } from '../../hooks/usePerformanceManager';

interface WaveformDisplayProps {
  audioLevel: number;
  isRecording: boolean;
  isPaused: boolean;
  height?: number;
  barCount?: number;
  className?: string;
  enablePerformanceOptimizations?: boolean;
}

const WaveformDisplay: React.FC<WaveformDisplayProps> = ({
  audioLevel,
  isRecording,
  isPaused,
  height = 60,
  barCount = 40,
  className = '',
  enablePerformanceOptimizations = true,
}) => {
  const performanceManager = usePerformanceManager({ autoStart: enablePerformanceOptimizations });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const barsRef = useRef<number[]>(new Array(barCount).fill(0));
  const lastUpdateTime = useRef<number>(0);
  const frameSkipCounter = useRef<number>(0);

  // Use refs for values that change frequently to avoid effect re-runs
  const audioLevelRef = useRef(audioLevel);
  const isRecordingRef = useRef(isRecording);
  const isPausedRef = useRef(isPaused);
  audioLevelRef.current = audioLevel;
  isRecordingRef.current = isRecording;
  isPausedRef.current = isPaused;

  // Get performance-aware settings
  const shouldEnableOptimizations = enablePerformanceOptimizations && performanceManager.qualitySettings;
  const targetFPS = shouldEnableOptimizations ? performanceManager.qualitySettings?.animationFPS || 30 : 60;
  const frameInterval = 1000 / targetFPS;
  const performanceStatus = performanceManager.performanceStatus || 'good';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = () => {
      const now = performance.now();
      
      // Performance-aware frame skipping
      if (shouldEnableOptimizations && (now - lastUpdateTime.current) < frameInterval) {
        animationFrameRef.current = requestAnimationFrame(draw);
        return;
      }
      
      // Additional frame skipping for poor performance
      if (performanceStatus === 'poor') {
        frameSkipCounter.current++;
        if (frameSkipCounter.current % 2 !== 0) {
          animationFrameRef.current = requestAnimationFrame(draw);
          return;
        }
      }
      
      lastUpdateTime.current = now;
      
      const { width, height: canvasHeight } = canvas;
      
      // Performance optimization: Use requestIdleCallback for non-critical updates
      const isHighPerformance = !shouldEnableOptimizations || performanceStatus === 'good';
      
      // Clear canvas
      ctx.fillStyle = '#0e0e0d'; // surface-container-lowest
      ctx.fillRect(0, 0, width, canvasHeight);

      const barWidth = width / barCount;
      const barSpacing = barWidth * 0.1;
      const actualBarWidth = barWidth - barSpacing;

      // Read current values from refs (avoids stale closures)
      const currentAudioLevel = audioLevelRef.current;
      const currentIsRecording = isRecordingRef.current;
      const currentIsPaused = isPausedRef.current;

      // Update bars based on audio level
      if (currentIsRecording && !currentIsPaused) {
        // Shift existing bars to the left
        for (let i = 0; i < barCount - 1; i++) {
          barsRef.current[i] = barsRef.current[i + 1];
        }

        // Add new bar based on current audio level
        const baseLevel = Math.max(0.05, currentAudioLevel * 0.9);
        const randomVariation = isHighPerformance ? Math.random() * 0.1 : 0.05;
        const newBarHeight = Math.min(1.0, baseLevel + randomVariation);
        barsRef.current[barCount - 1] = newBarHeight;

      } else if (!currentIsRecording || currentIsPaused) {
        // Gradually fade out bars when not recording
        for (let i = 0; i < barCount; i++) {
          barsRef.current[i] *= 0.95;
        }
      }

      // Draw bars
      for (let i = 0; i < barCount; i++) {
        const barHeight = barsRef.current[i] * canvasHeight * 0.8;
        const x = i * barWidth + barSpacing / 2;
        const y = (canvasHeight - barHeight) / 2;

        // Color gradient based on bar height and position
        const intensity = barsRef.current[i];
        let color;

        if (currentIsPaused) {
          color = `rgba(255, 226, 171, ${intensity})`; // secondary (#ffe2ab)
        } else if (currentIsRecording) {
          if (intensity > 0.7) {
            color = `rgba(255, 127, 80, ${intensity})`; // primary-container (#ff7f50)
          } else if (intensity > 0.4) {
            color = `rgba(255, 181, 156, ${intensity})`; // primary (#ffb59c)
          } else {
            color = `rgba(255, 226, 171, ${intensity})`; // secondary (#ffe2ab)
          }
        } else {
          color = `rgba(166, 139, 130, ${intensity})`; // outline (#a68b82)
        }

        ctx.fillStyle = color;
        ctx.fillRect(x, y, actualBarWidth, barHeight);

        // Add glow effect for active recording (only on high performance)
        if (isHighPerformance && currentIsRecording && !currentIsPaused && intensity > 0.5) {
          ctx.shadowColor = color;
          ctx.shadowBlur = 10;
          ctx.fillRect(x, y, actualBarWidth, barHeight);
          ctx.shadowBlur = 0;
        }
      }

      if (currentIsRecording || barsRef.current.some(bar => bar > 0.01)) {
        animationFrameRef.current = requestAnimationFrame(draw);
      } else if (shouldEnableOptimizations) {
        // Clean up animation frame when not needed to save resources
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      }
    };

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    // Start animation
    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, barCount, shouldEnableOptimizations, targetFPS, performanceStatus]);

  return (
    <div className={`relative ${className}`} data-testid="waveform-display">
      <canvas
        ref={canvasRef}
        className="w-full rounded-lg bg-surface-container-lowest"
        style={{ height: `${height}px` }}
        data-testid="waveform-canvas"
      />
      
      {/* Recording indicator */}
      {isRecording && (
        <div className="absolute top-2 right-2">
          <div className={`w-3 h-3 rounded-full ${
            isPaused
              ? 'bg-secondary animate-pulse'
              : 'bg-error animate-pulse-fast'
          }`} />
        </div>
      )}
      
      {/* Center line */}
      <div 
        className="absolute left-0 right-0 border-t border-outline-variant opacity-30" 
        style={{ top: `${height / 2}px` }}
      />
    </div>
  );
};

export default WaveformDisplay;